import { InfluencerLocationInput, InfluencerLocation, GoogleMapsSearchResult } from '@foodfluence/shared';
import { reverseGeocode, type SearchResult } from '../services/api/location';

export type { SearchResult };

/**
 * Returns the N locations closest to the given coordinates, sorted by distance ascending.
 * Falls back to the first `limit` entries if no coordinates are provided.
 */
export const getClosestLocations = <T extends { lat: number; lng: number }>(
  locations: T[],
  userCoords: { lat: number; lng: number } | null,
  limit: number
): T[] => {
  if (locations.length <= limit) return locations;
  if (!userCoords) return locations.slice(0, limit);
  return [...locations]
    .sort((a, b) =>
      calculateDistance(userCoords.lat, userCoords.lng, a.lat, a.lng) -
      calculateDistance(userCoords.lat, userCoords.lng, b.lat, b.lng)
    )
    .slice(0, limit);
};

export const getInfluencerPrimaryLocation = (influencerLocations: InfluencerLocation[]): InfluencerLocation | null => {
  return influencerLocations.find((l) => l.locationPriority === 1) ?? influencerLocations[0] ?? null;
};

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}; 

/**
 * Display text for a campaign's general areas (brandManaged "general locations" mode).
 * "Texas + 2 more" — whole-country selections render as the country name.
 */
export const getGeneralLocationDisplay = (
  allowedLocations: { [country: string]: string[] } | undefined
): string => {
  if (!allowedLocations) return '';
  const areas = Object.entries(allowedLocations).flatMap(([country, states]) =>
    states.includes('__WHOLE_COUNTRY__') ? [country] : states
  );
  if (areas.length === 0) return '';
  const remaining = areas.length - 1;
  return remaining > 0 ? `${areas[0]} + ${remaining} more` : areas[0];
};

export const getLocationDisplay = (
  locations: any[] | undefined,
  referenceLocation?: { lat: number; lng: number }
): string => {
  if (!locations || locations.length === 0) return '';

  let sortedLocations = [...locations];
  
  if (referenceLocation) {
    sortedLocations.sort((a, b) => {
      const distanceA = calculateDistance(
        referenceLocation.lat,
        referenceLocation.lng,
        a.lat,
        a.lng
      );
      const distanceB = calculateDistance(
        referenceLocation.lat,
        referenceLocation.lng,
        b.lat,
        b.lng
      );
      return distanceA - distanceB;
    });
  }
  
  const firstLocation = sortedLocations[0];
  const remainingCount = sortedLocations.length - 1;
  
  if (!firstLocation.city && firstLocation.state) {
    return `${firstLocation.state}`;
  }
  const locationText = `${firstLocation.city}${firstLocation.state ? `, ${firstLocation.state}` : ''}`;
  
  if (remainingCount > 0) {
    return `${locationText} + ${remainingCount} other${remainingCount === 1 ? '' : 's'}`;
  }
  
  return locationText;
};

export const defaultInfluencerLocation = {
  lat: 30.2672,
  lng: -97.7431,
  city: 'Austin',
  state: 'Texas',
  country: 'United States',
  maxCollabDistance: 25,
};

export const getCurrentLocation = (): Promise<InfluencerLocationInput> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 second timeout
      maximumAge: 300000 // 5 minute cache
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const data = await reverseGeocode(position.coords.latitude, position.coords.longitude);

          if (!data.address?.country) {
            reject(new Error('Could not determine location details'));
            return;
          }

          const locationInput: InfluencerLocationInput = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            country: data.address.country,
            city: data.address.city || data.address.town || data.address.county || data.address.village || '',
            state: data.address.country === 'United States' ? data.address.state : '',
            maxCollabDistance: 25,
          };

          // Only add zipCode if it exists in the response
          if (data.address.postcode) {
            locationInput.zipCode = data.address.postcode;
          }

          resolve(locationInput);
        } catch (error) {
          reject(error);
        }
      },
      (error) => {
        reject(error);
      },
      options
    );
  });
};

/**
 * Gets the user's current location, falling back to the default location if unavailable
 * @returns Promise<InfluencerLocationInput> - Either the user's current location or default location
 */
export const getLocationWithFallback = async (): Promise<InfluencerLocationInput> => {
  try {
    const currentLocation = await getCurrentLocation();
    return currentLocation;
  } catch (error) {
    console.error("Error getting current location:", error);
    return defaultInfluencerLocation;
  }
};

/**
 * Creates a complete InfluencerLocation object with default values for missing properties
 */
export const createCompleteLocation = (data?: InfluencerLocation): InfluencerLocation => {
  return {
    lat: data?.lat || 0,
    lng: data?.lng || 0,
    city: data?.city || '',
    state: data?.state || '',
    country: data?.country || '',
    zipCode: data?.zipCode,
    maxCollabDistance: data?.maxCollabDistance || 25,
    docId: data?.docId || '',
    influencerId: data?.influencerId || '',
    geohash: data?.geohash || '',
    isPredicted: data?.isPredicted || false
  };
};

/**
 * Formats location data for display in search queries
 */
export const formatLocationForSearch = (location: InfluencerLocation) => {
  if (!location.city && !location.state && !location.country) {
    return '';
  }
  
  if (location.country === 'United States') {
    return `${location.city || ''}${location.state ? `, ${location.state}` : ''}`;
  } else {
    return `${location.city || ''}${location.city ? ', ' : ''}${location.country || ''}`;
  }
};

/**
 * Calculates appropriate zoom level based on collaboration distance
 */
export const getZoomLevel = (maxCollabDistance: number) => {
  const d = maxCollabDistance || 25;
  if (d <= 10) return 10;
  if (d <= 25) return 9;
  if (d <= 50) return 8;
  if (d <= 100) return 7;
  return 6;
};

/**
 * Converts search result to InfluencerLocation format
 */
export const searchResultToLocation = (result: SearchResult, currentLocation: InfluencerLocation): InfluencerLocation => {
  return {
    ...currentLocation,
    lat: parseFloat(result.lat),
    lng: parseFloat(result.lon),
    country: result.address.country || '',
    city: result.address.city || result.address.town || result.address.county || result.address.village || '',
    state: result.address.country === 'United States' ? result.address.state || '' : '',
    zipCode: result.address.postcode
  };
};

/**
 * Converts Google Maps search result to InfluencerLocation format
 */
export const googleMapsResultToLocation = (
  result: GoogleMapsSearchResult, 
  currentLocation: InfluencerLocation
): InfluencerLocation => {
  return {
    ...currentLocation,
    lat: parseFloat(result.lat),
    lng: parseFloat(result.lng),
    country: result.country,
    city: result.city || '',
    state: result.state || '',
    zipCode: result.postalCode
  };
};

/**
 * Gets location display text for UI
 */
export const getLocationDisplayText = (location: InfluencerLocation) => {
  if (!location.city && !location.country) {
    return 'Click on the map to set location';
  }
  
  if (location.country === 'United States') {
    return `${location.city || ''}${location.state ? `, ${location.state}` : ''}`;
  } else {
    return `${location.city || ''}${location.city ? ', ' : ''}${location.country || ''}`;
  }
};

/**
 * Checks if two locations have the same city, state, and country
 */
export const noSameLocation = (locations: InfluencerLocation[]) => {
  // Check for duplicates by comparing city, state, and country
  for (let i = 0; i < locations.length; i++) {
    for (let j = i + 1; j < locations.length; j++) {
      if (
        locations[i].city === locations[j].city &&
        locations[i].state === locations[j].state &&
        locations[i].country === locations[j].country && 
        locations[i].zipCode === locations[j].zipCode
      ) {
        return false; // return false when duplicates are found
      }
    }
  }
  return true; // return true when no duplicates are found
};

/**
 * Filters out empty locations (locations with lat/lng of 0) from the end of an array
 */
export const filterEmptyLocations = (locations: InfluencerLocationInput[] | null): InfluencerLocationInput[] => {
  if (!locations || locations.length === 0) {
    return [];
  }
  
  let finalLocations = locations;
  if (locations.length > 1 && locations[locations.length - 1].lat === 0 && locations[locations.length - 1].lng === 0) {
    finalLocations = locations.slice(0, -1);
  }
  
  return finalLocations;
};

/**
 * Compares two location arrays to determine if they have changed
 */
export const locationsHaveChanged = (
  formLocations: InfluencerLocationInput[] | null, 
  originalLocations: InfluencerLocation[] | null
): boolean => {
  const finalLocations = filterEmptyLocations(formLocations);
  const convertedOriginalLocations = originalLocations || [];
  
  // Different array lengths means it changed
  if (finalLocations.length !== convertedOriginalLocations.length) {
    return true;
  }
  
  // Check if any corresponding locations have different values
  return finalLocations.some((loc, index) => {
    return locationHasChanged(loc, convertedOriginalLocations[index]);
  });
};

/**
 * Checks if a location has valid coordinates (not 0,0)
 */
export const hasValidCoordinates = (location: InfluencerLocation): boolean => {
  return location.lat !== 0 && location.lng !== 0;
};

/**
 * Compares two individual locations to see if they have changed
 */
export const locationHasChanged = (current: InfluencerLocationInput, original: InfluencerLocation): boolean => {
  return (
    current.lat !== original.lat ||
    current.lng !== original.lng ||
    current.city !== original.city ||
    current.state !== original.state ||
    current.country !== original.country ||
    current.maxCollabDistance !== original.maxCollabDistance
  );
};

/**
 * Determines if location changes should be considered as "changed" based on editing context
 */
export const shouldConsiderLocationChanged = (
  currentLocation: InfluencerLocation, 
  originalLocation: InfluencerLocation, 
  isEditing: boolean
): boolean => {
  // For new locations, consider it changed if location is set
  if (!isEditing) {
    return hasValidCoordinates(currentLocation);
  }
  
  // For editing existing locations, check for actual changes
  return locationHasChanged(currentLocation, originalLocation);
};